<template>
  <section class="section">
    <nav class="level" style="margin: 20px 0; align-items: flex-end;">
      <div class="level-left">
        <div class="level-item" style="flex-direction: column; align-items: flex-start;">
          <h1 class="title is-5">Precios de partners</h1>
          <div class="filter-bar">
            <div class="select">
              <select v-model="partnerId" @change="onPartnerChange">
                <option :value="null" disabled>Elegí un partner</option>
                <option v-for="p in partners" :key="p.id" :value="p.id">
                  {{ p.name }}{{ p.uses_own_prices ? ' · lista propia' : '' }}
                </option>
              </select>
            </div>
            <template v-if="selectedPartner">
              <input
                id="switchOwnPrices"
                type="checkbox"
                class="switch is-rounded is-info"
                :checked="selectedPartner.uses_own_prices"
                :disabled="isSavingPartner"
                @change="toggleOwnPrices($event)"
              >
              <label for="switchOwnPrices">Usa lista de precios propia</label>
            </template>
          </div>
          <p v-if="selectedPartner" class="results-count">
            <strong>{{ selectedPartner.current_prices }}</strong> precios vigentes
            <span v-if="selectedPartner.valid_to"> · vigencia {{ formatDate(selectedPartner.valid_from) }} al {{ formatDate(selectedPartner.valid_to) }}</span>
          </p>
        </div>
      </div>

      <div class="level-right" v-if="selectedPartner">
        <div class="buttons">
          <button class="button is-success" @click="openUploadModal">
            Subir lista
          </button>
        </div>
      </div>
    </nav>

    <article v-if="selectedPartner && !selectedPartner.uses_own_prices && selectedPartner.active_prices" class="message is-warning is-small">
      <div class="message-body">
        {{ selectedPartner.name }} tiene una lista cargada pero no la usa: sus productos se siguen cotizando con el algoritmo.
      </div>
    </article>

    <template v-if="selectedPartner">
      <div class="tabs is-boxed">
        <ul>
          <li :class="{ 'is-active': tab === 'prices' }" @click="tab = 'prices'"><a>Lista</a></li>
          <li :class="{ 'is-active': tab === 'imports' }" @click="tab = 'imports'"><a>Historial de cargas</a></li>
        </ul>
      </div>

      <div v-if="tab === 'prices'" class="box">
        <div class="filter-bar" style="margin-bottom: 12px;">
          <input class="input" type="text" placeholder="Buscar modelo..." v-model="filters.search" style="width: 220px;">
          <div class="select">
            <select v-model="filters.brand">
              <option value="">Todas las marcas</option>
              <option v-for="b in availableBrands" :key="b" :value="b">{{ b }}</option>
            </select>
          </div>
          <div class="select">
            <select v-model="filters.validity" @change="loadPrices">
              <option value="current">Vigentes</option>
              <option value="upcoming">Próximas</option>
              <option value="expired">Vencidas</option>
              <option value="all">Todas</option>
            </select>
          </div>
          <span class="results-count">Mostrando <strong>{{ filteredPrices.length }}</strong> de {{ prices.length }}</span>
        </div>
        <base-table
          class="is-bordered is-striped is-narrow is-fullwidth is-hoverable"
          :columns="priceColumns"
          :data="filteredPrices"
          :loading="isLoadingPrices"
        >
          <template v-slot:gradeA="{ row }">
            <span class="grade-pill grade-a">${{ formatMoney(row.price_a) }}</span>
          </template>
          <template v-slot:gradeAB="{ row }">
            <span class="grade-pill grade-ab">${{ formatMoney(row.price_ab) }}</span>
          </template>
          <template v-slot:gradeB="{ row }">
            <span class="grade-pill grade-b">${{ formatMoney(row.price_b) }}</span>
          </template>
          <template v-slot:gradeC="{ row }">
            <span class="grade-pill grade-c">${{ formatMoney(row.price_c) }}</span>
          </template>
        </base-table>
      </div>

      <div v-else class="box">
        <base-table
          class="is-bordered is-striped is-narrow is-fullwidth is-hoverable"
          :columns="importColumns"
          :data="imports"
          :loading="isLoadingImports"
        >
          <template v-slot:file="{ row }">
            <a v-if="row.file_url" :href="row.file_url" target="_blank" rel="noopener noreferrer">{{ row.file_name }}</a>
            <span v-else>{{ row.file_name }}</span>
          </template>
          <template v-slot:status="{ row }">
            <span class="tag" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
          </template>
          <template v-slot:actions="{ row }">
            <div class="buttons are-small is-centered">
              <button class="button is-small is-info is-light" @click="openImport(row.id)">Ver reporte</button>
              <button class="button is-small is-light" @click="downloadReport(row.id)">Descargar</button>
            </div>
          </template>
        </base-table>
      </div>
    </template>

    <p v-else-if="!isLoadingPartners" class="has-text-grey has-text-centered" style="padding: 2rem;">
      Elegí un partner para ver su lista de precios.
    </p>

    <div class="modal" :class="{ 'is-active': isUploadModalOpen }">
      <div class="modal-background" @click="closeUploadModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Subir lista de {{ selectedPartner && selectedPartner.name }}</p>
          <button class="delete" @click="closeUploadModal" :disabled="isUploading"></button>
        </header>
        <section class="modal-card-body">
          <div class="field">
            <label class="label">Archivo</label>
            <div class="file has-name is-fullwidth">
              <label class="file-label">
                <input class="file-input" type="file" accept=".xlsx,.xls,.csv" @change="onFileSelected">
                <span class="file-cta"><span class="file-label">Elegir archivo…</span></span>
                <span class="file-name">{{ uploadFile ? uploadFile.name : 'Lista con columnas Modelo, Marca, Desde y Hasta' }}</span>
              </label>
            </div>
          </div>
          <p class="help" style="margin-bottom: 12px;">
            La vigencia se lee del archivo. Completala acá solo si el archivo no la trae o querés pisarla.
          </p>
          <div class="columns">
            <div class="column field">
              <label class="label">Vigencia desde</label>
              <input class="input" type="date" v-model="uploadForm.validFrom">
            </div>
            <div class="column field">
              <label class="label">Vigencia hasta</label>
              <input class="input" type="date" v-model="uploadForm.validTo">
            </div>
          </div>
          <article class="message is-info is-small">
            <div class="message-body">
              Subir la lista no cambia precios: se genera un borrador con el reporte y los precios se aplican recién al publicarlo.
            </div>
          </article>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" :class="{ 'is-loading': isUploading }" :disabled="!uploadFile || isUploading" @click="upload">
            Revisar
          </button>
          <button class="button" @click="closeUploadModal" :disabled="isUploading">Cancelar</button>
        </footer>
      </div>
    </div>

    <div class="modal" :class="{ 'is-active': !!report }">
      <div class="modal-background" @click="closeReport"></div>
      <div class="modal-card report-card" v-if="report">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Carga #{{ report.id }} · {{ report.file_name }}
            <span class="tag" :class="statusClass(report.status)" style="margin-left: 8px;">{{ statusLabel(report.status) }}</span>
          </p>
          <button class="delete" @click="closeReport" :disabled="isPublishing"></button>
        </header>
        <section class="modal-card-body">
          <p class="is-size-7 has-text-grey" style="margin-bottom: 10px;">
            Vigencia {{ formatDate(report.valid_from) }} al {{ formatDate(report.valid_to) }}
            · subido por {{ report.uploaded_by_name || '—' }} el {{ report.created_at }}
            <span v-if="report.published_at"> · publicado el {{ report.published_at }}</span>
            · A = Hasta, C = Desde, AB y B en tercios del rango
          </p>

          <div class="counters">
            <div class="counter"><span>{{ report.report.totalRows }}</span>filas</div>
            <div class="counter is-new"><span>{{ report.rows_new }}</span>nuevas</div>
            <div class="counter is-updated"><span>{{ report.rows_updated }}</span>actualizadas</div>
            <div class="counter"><span>{{ report.rows_unchanged }}</span>sin cambios</div>
            <div class="counter is-removed"><span>{{ report.rows_removed }}</span>bajas</div>
            <div class="counter is-rejected"><span>{{ report.rows_rejected }}</span>rechazadas</div>
          </div>

          <template v-if="report.report.warnings && report.report.warnings.length">
            <p class="report-heading">Avisos (no bloquean)</p>
            <ul class="warnings">
              <li v-for="(w, i) in report.report.warnings" :key="i">
                <strong v-if="w.row">Fila {{ w.row }}</strong> <span v-if="w.model">{{ w.model }}:</span> {{ w.message }}
              </li>
            </ul>
          </template>

          <template v-if="report.report.rows && report.report.rows.length">
            <p class="report-heading">Aceptadas: cómo se leyó cada fila</p>
            <div class="table-container scroll">
              <table class="table is-fullwidth is-narrow is-striped">
                <thead><tr><th>Fila</th><th>En la lista</th><th>Modelo del catálogo</th><th>RAM</th><th>Almac.</th><th>Desde</th><th>Hasta</th><th v-for="g in grades" :key="g">{{ g }}</th></tr></thead>
                <tbody>
                  <tr v-for="r in report.report.rows" :key="r.row">
                    <td>{{ r.row }}</td><td>{{ r.raw }}</td><td>{{ r.label }}</td><td>{{ gb(r.ramGb) }}</td><td>{{ gb(r.storageGb) }}</td>
                    <td>{{ r.source ? formatMoney(r.source.desde) : '—' }}</td><td>{{ r.source ? formatMoney(r.source.hasta) : '—' }}</td>
                    <td v-for="g in grades" :key="g">{{ formatMoney(r.prices[g]) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template v-if="report.report.changes && report.report.changes.length">
            <p class="report-heading">Cambios de precio</p>
            <div class="table-container scroll">
              <table class="table is-fullwidth is-narrow is-striped">
                <thead><tr><th>Fila</th><th>Modelo</th><th>RAM</th><th>Almac.</th><th>A</th><th>AB</th><th>B</th><th>C</th><th>%</th></tr></thead>
                <tbody>
                  <tr v-for="c in report.report.changes" :key="`${c.row}-${c.model}`">
                    <td>{{ c.row }}</td><td>{{ c.model }}</td><td>{{ gb(c.ramGb) }}</td><td>{{ gb(c.storageGb) }}</td>
                    <td v-for="g in grades" :key="g">
                      <span class="old-val">{{ formatMoney(c.before[g]) }}</span><span class="arrow">→</span><span class="new-val">{{ formatMoney(c.after[g]) }}</span>
                    </td>
                    <td>{{ c.pct }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template v-if="report.report.removed && report.report.removed.length">
            <p class="report-heading">Bajas: estaban en la lista y no vinieron (o vinieron rechazadas)</p>
            <div class="table-container scroll">
              <table class="table is-fullwidth is-narrow is-striped">
                <thead><tr><th>Modelo</th><th>RAM</th><th>Almac.</th><th>Precio A</th></tr></thead>
                <tbody>
                  <tr v-for="r in report.report.removed" :key="r.partnerPriceId">
                    <td>{{ r.model }}</td><td>{{ gb(r.ramGb) }}</td><td>{{ gb(r.storageGb) }}</td><td>${{ formatMoney(r.before.A) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template v-if="report.report.rejected && report.report.rejected.length">
            <p class="report-heading">Rechazadas por motivo</p>
            <table class="table is-fullwidth is-narrow">
              <tbody>
                <tr v-for="(count, reason) in rejectedByReason" :key="reason"><td>{{ reason }}</td><td class="has-text-right"><strong>{{ count }}</strong></td></tr>
              </tbody>
            </table>
            <div class="table-container scroll">
              <table class="table is-fullwidth is-narrow is-striped">
                <thead><tr><th>Fila</th><th>Marca</th><th>Modelo</th><th>RAM</th><th>Almac.</th><th>Motivo</th></tr></thead>
                <tbody>
                  <tr v-for="r in report.report.rejected" :key="r.row">
                    <td>{{ r.row }}</td><td>{{ r.brand }}</td><td>{{ r.model }}</td><td>{{ gb(r.ramGb) }}</td><td>{{ gb(r.storageGb) }}</td><td>{{ r.reason }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <article v-if="report.status === 'draft'" class="message is-warning is-small" style="margin-top: 12px;">
            <div class="message-body">
              Al publicar, la lista de {{ selectedPartner && selectedPartner.name }} queda exactamente como este archivo:
              las filas rechazadas y las que no vinieron se dan de baja. Los productos ya cotizados conservan su precio.
            </div>
          </article>
        </section>
        <footer class="modal-card-foot">
          <template v-if="report.status === 'draft'">
            <button class="button is-success" :class="{ 'is-loading': isPublishing }" :disabled="isPublishing" @click="publish">Publicar</button>
            <button class="button is-danger is-light" :disabled="isPublishing" @click="discard">Descartar</button>
          </template>
          <button class="button is-light" @click="downloadReport(report.id)">Descargar reporte</button>
          <a v-if="report.file_url" class="button is-light" :href="report.file_url" target="_blank" rel="noopener noreferrer">Archivo original</a>
          <button class="button" @click="closeReport" :disabled="isPublishing">Cerrar</button>
        </footer>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'

const STATUS = {
  draft: { label: 'Borrador', class: 'is-warning' },
  published: { label: 'Publicada', class: 'is-success' },
  discarded: { label: 'Descartada', class: 'is-light' }
}

export default {
  name: 'PartnerPrices',
  components: {
    BaseTable: () => import('@/components/Table')
  },
  data () {
    return {
      grades: ['A', 'AB', 'B', 'C'],
      partners: [],
      partnerId: null,
      tab: 'prices',
      prices: [],
      imports: [],
      filters: { search: '', brand: '', validity: 'current' },
      isLoadingPartners: false,
      isLoadingPrices: false,
      isLoadingImports: false,
      isSavingPartner: false,
      isUploadModalOpen: false,
      isUploading: false,
      isPublishing: false,
      uploadFile: null,
      uploadForm: { validFrom: '', validTo: '' },
      report: null,
      priceColumns: [
        { id: 'brand', header: 'Marca' },
        { id: 'model', header: 'Modelo', class: 'col-model' },
        { id: 'ram_gb', header: 'RAM', class: 'has-text-centered', accessor: row => this.gb(row.ram_gb) },
        { id: 'storage_gb', header: 'Almacenamiento', class: 'has-text-centered', accessor: row => this.gb(row.storage_gb) },
        { id: 'price_a', name: 'gradeA', header: 'Precio A', class: 'has-text-centered' },
        { id: 'price_ab', name: 'gradeAB', header: 'Precio AB', class: 'has-text-centered' },
        { id: 'price_b', name: 'gradeB', header: 'Precio B', class: 'has-text-centered' },
        { id: 'price_c', name: 'gradeC', header: 'Precio C', class: 'has-text-centered' },
        { id: 'validity', header: 'Vigencia', class: 'has-text-centered', accessor: row => `${this.formatDate(row.valid_from)} al ${this.formatDate(row.valid_to)}` },
        { id: 'import_id', header: 'Carga', class: 'has-text-centered', accessor: row => `#${row.import_id}` }
      ],
      importColumns: [
        { id: 'id', header: '#', class: 'has-text-centered' },
        { id: 'created_at', header: 'Fecha' },
        { id: 'file', name: 'file', header: 'Archivo' },
        { id: 'uploaded_by_name', header: 'Subido por', accessor: row => row.uploaded_by_name || '—' },
        { id: 'validity', header: 'Vigencia', accessor: row => `${this.formatDate(row.valid_from)} al ${this.formatDate(row.valid_to)}` },
        { id: 'status', name: 'status', header: 'Estado', class: 'has-text-centered' },
        { id: 'counts', header: 'Nuevas / act. / igual / bajas / rech.', class: 'has-text-centered', accessor: row => [row.rows_new, row.rows_updated, row.rows_unchanged, row.rows_removed, row.rows_rejected].join(' / ') },
        { id: 'actions', name: 'actions', header: 'Acciones', class: 'has-text-centered' }
      ]
    }
  },
  computed: {
    selectedPartner () {
      return this.partners.find(p => p.id === this.partnerId) || null
    },
    availableBrands () {
      return [...new Set(this.prices.map(p => p.brand))].sort((a, b) => a.localeCompare(b))
    },
    filteredPrices () {
      const term = this.filters.search.trim().toLowerCase()
      return this.prices.filter(p => {
        if (this.filters.brand && p.brand !== this.filters.brand) return false
        if (term && !`${p.brand} ${p.model}`.toLowerCase().includes(term)) return false
        return true
      })
    },
    rejectedByReason () {
      return (this.report?.report?.rejected || []).reduce((acc, r) => ({ ...acc, [r.reason]: (acc[r.reason] || 0) + 1 }), {})
    }
  },
  methods: {
    ...mapActions([
      'getPartnersDB',
      'updatePartnerDB',
      'getPartnerPricesDB',
      'importPartnerPricesDB',
      'getPartnerPriceImportsDB',
      'getPartnerPriceImportDB',
      'downloadPartnerPriceReportDB',
      'publishPartnerPriceImportDB',
      'discardPartnerPriceImportDB',
      'addTemporalNotification'
    ]),
    formatMoney (value) {
      if (value === null || value === undefined || value === '') return '—'
      return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    formatDate (value) {
      if (!value) return '—'
      const [y, m, d] = String(value).slice(0, 10).split('-')
      return `${d}/${m}/${y}`
    },
    gb (value) {
      if (!value) return '—'
      return value >= 1024 && value % 1024 === 0 ? `${value / 1024} TB` : `${value} GB`
    },
    statusLabel (status) {
      return STATUS[status]?.label || status
    },
    statusClass (status) {
      return STATUS[status]?.class || ''
    },
    saveFile ({ blob, fileName }) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    async loadPartners () {
      this.isLoadingPartners = true
      const partners = await this.getPartnersDB()
      if (partners) this.partners = partners
      this.isLoadingPartners = false
    },
    async loadPrices () {
      if (!this.partnerId) return
      this.isLoadingPrices = true
      const rows = await this.getPartnerPricesDB({ partnerId: this.partnerId, filters: { validity: this.filters.validity } })
      this.prices = rows || []
      this.isLoadingPrices = false
    },
    async loadImports () {
      if (!this.partnerId) return
      this.isLoadingImports = true
      const rows = await this.getPartnerPriceImportsDB(this.partnerId)
      this.imports = rows || []
      this.isLoadingImports = false
    },
    async refresh () {
      await Promise.all([this.loadPartners(), this.loadPrices(), this.loadImports()])
    },
    onPartnerChange () {
      this.filters = { search: '', brand: '', validity: 'current' }
      if (this.$route.query.partner !== String(this.partnerId)) {
        this.$router.replace({ query: { partner: this.partnerId } }).catch(() => {})
      }
      this.loadPrices()
      this.loadImports()
    },
    async toggleOwnPrices (event) {
      const value = event.target.checked
      const partner = this.selectedPartner
      if (value && !partner.current_prices) {
        const ok = window.confirm(`${partner.name} no tiene precios vigentes: con la lista propia prendida ningún equipo va a poder cargarse con su código. ¿Prender igual?`)
        if (!ok) {
          event.target.checked = partner.uses_own_prices
          return
        }
      }
      this.isSavingPartner = true
      const updated = await this.updatePartnerDB({ partnerId: partner.id, data: { uses_own_prices: value } })
      this.isSavingPartner = false
      if (updated) partner.uses_own_prices = updated.uses_own_prices
      event.target.checked = partner.uses_own_prices
    },
    async downloadReport (importId) {
      const file = await this.downloadPartnerPriceReportDB(importId)
      if (file) this.saveFile(file)
    },
    openUploadModal () {
      this.uploadFile = null
      this.uploadForm = { validFrom: '', validTo: '' }
      this.isUploadModalOpen = true
    },
    closeUploadModal () {
      if (this.isUploading) return
      this.isUploadModalOpen = false
    },
    onFileSelected (event) {
      this.uploadFile = (event.target.files && event.target.files[0]) || null
    },
    async upload () {
      this.isUploading = true
      const draft = await this.importPartnerPricesDB({
        partnerId: this.partnerId,
        file: this.uploadFile,
        validFrom: this.uploadForm.validFrom,
        validTo: this.uploadForm.validTo
      })
      this.isUploading = false
      if (!draft) return
      this.isUploadModalOpen = false
      this.loadImports()
      await this.openImport(draft.id)
    },
    async openImport (importId) {
      const detail = await this.getPartnerPriceImportDB(importId)
      if (detail) this.report = { ...detail, report: detail.report || {} }
    },
    closeReport () {
      if (this.isPublishing) return
      this.report = null
    },
    async publish () {
      this.isPublishing = true
      const published = await this.publishPartnerPriceImportDB(this.report.id)
      this.isPublishing = false
      if (!published) return
      this.report = null
      this.tab = 'prices'
      await this.refresh()
    },
    async discard () {
      const discarded = await this.discardPartnerPriceImportDB(this.report.id)
      if (!discarded) return
      this.report = null
      await this.loadImports()
    }
  },
  async mounted () {
    await this.loadPartners()
    const fromQuery = Number(this.$route.query.partner)
    const initial = this.partners.find(p => p.id === fromQuery) || this.partners.find(p => p.uses_own_prices)
    if (initial) {
      this.partnerId = initial.id
      this.onPartnerChange()
    }
  }
}
</script>

<style scoped>
.section { width: 100%; padding: 0 20px; }

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
}

.results-count {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.results-count strong { color: #00947e; }

::v-deep td.col-model {
  font-weight: 600;
  color: #363636;
}

.grade-pill {
  display: inline-block;
  min-width: 78px;
  padding: 0.2em 0.6em;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
}

.grade-a { background: #00947e; color: #fff; }
.grade-ab { background: #00d1b2; color: #fff; }
.grade-b { background: #fff5e0; color: #946c00; }
.grade-c { background: #fdeaea; color: #a8322d; }

.report-card {
  width: 1000px;
  max-width: 95vw;
}

.counters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.counter {
  flex: 1;
  min-width: 110px;
  padding: 10px;
  border-radius: 8px;
  background: #f5f5f5;
  text-align: center;
  font-size: 12px;
  color: #666;
}

.counter span {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #363636;
}

.counter.is-new { background: #e8f8f0; }
.counter.is-updated { background: #eef4ff; }
.counter.is-removed { background: #fff5e0; }
.counter.is-rejected { background: #fdeaea; }

.report-heading {
  margin: 16px 0 6px;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7a7a7a;
}

.scroll {
  max-height: 260px;
  overflow-y: auto;
}

.warnings {
  font-size: 13px;
  list-style: disc;
  padding-left: 20px;
}

.old-val {
  color: #b5b5b5;
  text-decoration: line-through;
  font-size: 12px;
}

.arrow { margin: 0 4px; color: #999; }

.new-val {
  font-weight: 700;
  color: #00947e;
}
</style>
